import { Injectable, signal, inject } from '@angular/core';
import { User } from '../types/user-type.type';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { LoginService } from './login.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private router = inject(Router);
  private loginService = inject(LoginService);

  // Use a interface User correta
  currentUserSig = signal<User | undefined | null>(undefined);

  constructor(private http: HttpClient) {
    // Carrega usuário do storage ao inicializar
    this.loadUserFromStorage();
  }

  /**
   * Verifica se o usuário está logado
   */
  isLoggedIn(): boolean {
    return this.loginService.isLoggedIn();
  }

  /**
   * Obtém o token JWT de acesso
   */
  getAccessToken(): string | null {
    return this.loginService.getToken();
  }

  /**
   * Obtém o refresh token
   */
  getRefreshToken(): string | null {
    return sessionStorage.getItem('refresh-token');
  }

  /**
   * Salva o token de acesso
   */
  setAccessToken(token: string): void {
    sessionStorage.setItem('auth-token', token);
  }

  /**
   * Salva o refresh token
   */
  setRefreshToken(token: string): void {
    sessionStorage.setItem('refresh-token', token);
  }

  /**
   * Obtém o short name do usuário
   */
  getShortName(): string | null {
    return this.loginService.getShortName();
  }

  /**
   * Obtém os roles do usuário
   */
  getRoles(): string[] {
    return this.loginService.getRoles();
  }

  /**
   * Verifica se o token expirou.
   */
  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return true;
    }

    try {
      const payload = this.decodeToken(token);
      if (payload && payload.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        return currentTime > payload.exp;
      }
      return true;
    } catch (error) {
      console.error('Erro ao decodificar o token:', error);
      return true;
    }
  }

  /**
   * Decodifica um token JWT.
   */
  private decodeToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return null;
    }
  }

  /**
   * Obtém informações do usuário a partir do token
   */
  getUserFromToken(): any {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      return this.decodeToken(token);
    } catch (error) {
      console.error('Erro ao obter usuário do token:', error);
      return null;
    }
  }

  /**
   * Faz refresh do token usando o refresh token
   */
  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('Refresh token não disponível');
    }

    return this.http.post(`${this.apiUrl}/auth/refresh`, { refreshToken });
  }

  /**
   * Processa resposta de login bem-sucedido
   */
  handleLoginSuccess(response: any, cpf: string): void {
    // Seu LoginService já salva os dados no sessionStorage
    // Aqui atualizamos o signal do usuário
    
    // Se a resposta tiver refresh token, salva
    if (response.refreshToken) {
      this.setRefreshToken(response.refreshToken);
    }
    
    // Atualiza o signal do usuário atual
    this.updateCurrentUser(cpf, response);
  }

  /**
   * Atualiza o usuário atual no signal
   */
  private updateCurrentUser(cpf: string, response?: any): void {
    const userData = this.getUserFromToken();
    const shortName = this.getShortName() || '';
    const roles = this.getRoles() || [];
    
    // Extrai informações do token ou da resposta
    const tokenData = userData || {};
    const responseData = response?.data || response || {};
    
    // Cria o objeto User conforme sua interface
    const user: User = {
      id: tokenData.id || responseData.id || 0, // Use 0 como fallback se não tiver id
      shortName: shortName,
      name: tokenData.name || responseData.name || '', // Adicione esta propriedade ao token
      cpf: cpf, // Usa o CPF do login
      role: roles.length > 0 ? roles[0] : '', // Pega o primeiro role ou string vazia
      email: tokenData.email || responseData.email || '' // Adicione esta propriedade ao token
    };

    this.currentUserSig.set(user);
  }

  /**
   * Carrega usuário a partir dos dados armazenados
   */
  private loadUserFromStorage(): void {
    if (this.isLoggedIn() && !this.isTokenExpired()) {
      // Tenta recuperar o CPF do token ou sessionStorage
      const userData = this.getUserFromToken();
      const cpf = userData?.cpf || userData?.sub || '';
      
      if (cpf) {
        this.updateCurrentUser(cpf);
      } else {
        this.currentUserSig.set(null);
      }
    } else {
      this.currentUserSig.set(null);
    }
  }

  /**
   * Login com credenciais
   */
  login(credentials: { cpf: string; password: string }): Observable<any> {
    return this.loginService.login(credentials.cpf, credentials.password);
  }

  /**
   * Logout
   */
  logout(): void {
    const refreshToken = this.getRefreshToken();
    
    // Se tiver refresh token, invalida no servidor
    if (refreshToken) {
      this.http.post(`${this.apiUrl}/auth/logout`, { refreshToken })
        .subscribe({
          next: () => console.log('Logout realizado no servidor'),
          error: (err) => console.error('Erro ao fazer logout no servidor:', err)
        });
    }
    
    // Limpa dados localmente usando o LoginService existente
    this.loginService.logout();
    this.clearRefreshToken();
    
    // Limpa o signal
    this.currentUserSig.set(null);
    
    // Redireciona para login
    this.router.navigate(['/login']);
  }

  /**
   * Limpa apenas o refresh token
   */
  private clearRefreshToken(): void {
    sessionStorage.removeItem('refresh-token');
  }

  /**
   * Verifica se tem refresh token disponível
   */
  hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  /**
   * Obtém o usuário atual
   */
  getCurrentUser(): User | null {
    return this.currentUserSig() || null;
  }

  /**
   * Verifica se o usuário tem uma role específica
   */
  hasRole(role: string): boolean {
  const user = this.getCurrentUser();
  if (!user) return false;
  
  // Trata role como string ou array
  if (typeof user.role === 'string') {
    return user.role === role;
  }
  
  // Se for array
  if (Array.isArray(user.role)) {
    return user.role.includes(role);
  }
  
  // Se for undefined ou null
  return false;
}


  /**
   * Verifica se o usuário é admin
   */
 isAdmin(): boolean {
  const user = this.getCurrentUser();
  if (!user) return false;
  
  // Verifica múltiplas possibilidades de nome para admin
  const adminRoles = ['ROLE_ADMIN', 'ADMIN', 'Administrador', 'administrador'];
  
  if (typeof user.role === 'string') {
    return adminRoles.includes(user.role);
  }
  
  if (Array.isArray(user.role)) {
    return user.role.some(role => adminRoles.includes(role));
  }
  
  return false;
}

  /**
   * Debug - mostra todos os dados de autenticação
   */
  debugAuthData(): void {
    console.log('🐛 AuthService - Debug:');
    console.log('   Token:', this.getAccessToken());
    console.log('   Refresh Token:', this.getRefreshToken());
    console.log('   ShortName:', this.getShortName());
    console.log('   Roles:', this.getRoles());
    console.log('   IsLoggedIn:', this.isLoggedIn());
    console.log('   IsTokenExpired:', this.isTokenExpired());
    console.log('   Current User:', this.getCurrentUser());
  }
}